/**
 * Analytics Service - Tracks events only with user consent
 */

import { analytics } from '../config/firebase';
import { logEvent } from 'firebase/analytics';
import { hasConsentFor } from './cookieService';

// Track page views
export const trackPageView = (pageName, pageTitle = null) => {
  if (!hasConsentFor('analytics')) {
    console.log('Page view not tracked - no analytics consent');
    return;
  }

  try {
    logEvent(analytics, 'page_view', {
      page_title: pageTitle || pageName,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
    console.log(`Page view tracked: ${pageName}`);
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track user actions
export const trackEvent = (eventName, parameters = {}) => {
  if (!hasConsentFor('analytics')) {
    console.log(`Event "${eventName}" not tracked - no analytics consent`);
    return;
  }

  try {
    logEvent(analytics, eventName, parameters);
    console.log(`Event tracked: ${eventName}`, parameters);
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Track file uploads
export const trackFileUpload = (fileType, fileSize) => {
  trackEvent('file_upload', {
    file_type: fileType,
    file_size: fileSize
  });
};

// Track user registration/login
export const trackUserLogin = (method = 'google') => {
  trackEvent('login', {
    method: method
  });
};

// Track app analysis completion
export const trackAnalysisComplete = (fileId, analysisResults) => {
  trackEvent('analysis_complete', {
    file_id: fileId,
    analysis_duration: analysisResults.duration || null,
    success: analysisResults.status === 'completed'
  });
};

// Track subscription events
export const trackSubscription = (action, planType = null) => {
  const eventData = {
    action: action // 'subscribe', 'cancel', 'upgrade', 'downgrade'
  };
  
  if (planType) {
    eventData.plan_type = planType;
  }
  
  trackEvent('subscription_action', eventData);
};

// Track app deployment
export const trackAppDeployment = (fileId, status) => {
  trackEvent('app_deployment', {
    file_id: fileId,
    deployment_status: status
  });
};

// Track cookie consent changes
export const trackCookieConsent = (consentData) => {
  // This event should always be tracked, even without analytics consent
  // because it's about the consent itself
  try {
    if (analytics) {
      logEvent(analytics, 'cookie_consent_updated', {
        analytics_consent: consentData.analytics || false,
        marketing_consent: consentData.marketing || false,
        functional_consent: consentData.functional || false
      });
    }
  } catch (error) {
    console.error('Error tracking cookie consent:', error);
  }
};

// Set user properties (demographics, etc.)
export const setUserProperties = (properties) => {
  if (!hasConsentFor('analytics')) {
    console.log('User properties not set - no analytics consent');
    return;
  }

  try {
    // Firebase Analytics automatically handles user properties
    // You can add custom user properties here
    console.log('User properties would be set:', properties);
    
    // Example:
    // setUserProperties(analytics, {
    //   user_type: properties.userType,
    //   subscription_plan: properties.plan
    // });
    
  } catch (error) {
    console.error('Error setting user properties:', error);
  }
};

// Custom events for your app
export const trackCustomEvent = (category, action, label = null, value = null) => {
  const eventData = {
    event_category: category,
    event_action: action
  };
  
  if (label) eventData.event_label = label;
  if (value) eventData.value = value;
  
  trackEvent('custom_event', eventData);
}; 